// src/pages/command/components/CommandPaymentModal.jsx
import "./CommandPaymentModal.scss";

import { Alert, Button, Col, Form, ListGroup, Modal, Row } from "react-bootstrap";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  displayName,
  formatDate,
  formatDateTime,
  formatInReal,
  formatTransactionType,
} from "../../../commons/util/HallAppUtil";
import { useEffect, useMemo, useState } from "react";

import Accordion from "react-bootstrap/Accordion";
import AppButton from "../../../commons/buttons/AppButton";
import AppCurrencyInput from "../../../commons/inputs/AppCurrencyInput";
import AppInputSelect from "../../../commons/inputs/AppInputSelect";
import AppLoading from "../../../commons/AppLoading";
import CommandAPI from "../CommandAPI";
import ConfirmModal from "../../../commons/modal/ConfirmModal";
import CustomContainer from "../../../commons/containers/CustomContainer";
import CustomerRewardsAPI from "../../HallPoints/CustomerRewardsAPI";
import FloatingVideoButton from "../../../commons/buttons/FloatingVideoButton";
import HallAppCenterContainer from "../../../commons/containers/HallAppCenterContainer";
import HallAppToast from "../../../commons/HallAppToast";
import MachineAPI from "../../machine/MachineAPI";
import SchedulingAPI from "../../scheduler/SchedulingAPI";

const paymentOptions = [
  { value: "", label: "Selecione o tipo de pagamento" },
  { value: "CREDIT_CARD", label: "Crédito" },
  { value: "DEBIT_CARD", label: "Débito" },
  { value: "PIX", label: "PIX" },
  { value: "MONEY", label: "Dinheiro" },
];

const customerRewardsAPI = new CustomerRewardsAPI();
const machineAPI = new MachineAPI();
const schedulingAPI = new SchedulingAPI();

function benefitTypeLabel(type) {
  switch (String(type || "").toUpperCase()) {
    case "CREDIT":
      return "Crédito";
    case "DISCOUNT_PERCENT":
      return "Desconto (%)";
    case "DISCOUNT_AMOUNT":
      return "Desconto (R$)";
    case "INCLUDED_SERVICE":
      return "Serviço incluso";
    default:
      return type || "Benefício";
  }
}

function benefitValueLabel(b) {
  if (!b) return "";
  const t = String(b.type || "").toUpperCase();
  if (t === "DISCOUNT_PERCENT") return `${Number(b.value || 0)}%`;
  if (t === "CREDIT" || t === "DISCOUNT_AMOUNT") return formatInReal(Number(b.value || 0));
  return "";
}

function truncateText(text, maxLength = 26) {
  if (!text) return "";
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
}

function renderWithTooltip(text, maxLength = 26) {
  if (!text) return "—";

  const isTruncated = text.length > maxLength;
  const displayText = isTruncated ? `${text.slice(0, maxLength)}...` : text;

  if (!isTruncated) return displayText;

  return (
    <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${String(text || "")}`}>{text}</Tooltip>}>
      <span style={{ cursor: "pointer" }}>{displayText}</span>
    </OverlayTrigger>
  );
}

function CommandPaymentModal({ closeShow, show, command }) {
  const commandAPI = new CommandAPI();

  const [type, setType] = useState(null);
  const [value, setValue] = useState(null);

  const [transactions, setTransactions] = useState([]);
  const [isLoadingTransactions, setLoadingTransaction] = useState(true);
  const [totalPaid, setTotalPaid] = useState(0);

  const [showModalDelete, setShowModalDelete] = useState(false);
  const [isLoadingModalDelete, setLoadingModalDelete] = useState(false);
  const [transaction, setTransaction] = useState({});
  const [hasChange, setHasChange] = useState(false);

  const [isLoadingPoints, setIsLoadingPoints] = useState(true);
  const [points, setPoints] = useState(0);
  const [isLoadingUsableDays, setIsLoadingUsableDays] = useState(false);
  const [usableDays, setUsableDays] = useState([]);

  const [isLoadingMachine, setIsLoadingMachine] = useState(true);
  const [machines, setMachines] = useState([]);
  const [optionsMachines, setOptionsMachines] = useState([]);
  const [installment, setInstallment] = useState([]);
  const [installmentCount, setInstallmentCount] = useState(1);
  const [selectedMachineId, setSelectedMachineId] = useState(null);
  const [payer, setPayer] = useState("CLIENT");
  const [fee, setFee] = useState(0);

  const [isLoadingClubs, setIsLoadingClubs] = useState(true);
  const [clubs, setClubs] = useState([]);

  const [isLoadingPackages, setIsLoadingPackages] = useState(true);
  const [packages, setPackages] = useState([]);

  const [showBenefitModal, setShowBenefitModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedBenefitType, setSelectedBenefitType] = useState(null); // PACKAGE | CLUB | POINT
  const [selectedBenefitKey, setSelectedBenefitKey] = useState(null);
  const [isApplyingBenefit, setIsApplyingBenefit] = useState(false);

  const [showModalClubGeneric, setShowModalClubGeneric] = useState(false);
  const [selectedGenericBenefitKey, setSelectedGenericBenefitKey] = useState(null);
  const [isLoadingClubApply, setIsLoadingClubApply] = useState(false);

  const [showModalPackageGeneric, setShowModalPackageGeneric] = useState(false);
  const [selectedGenericPackageKey, setSelectedGenericPackageKey] = useState(null);
  const [isLoadingPackageApply, setIsLoadingPackageApply] = useState(false);

  useEffect(() => {
    if (!show || !command?.id) return;

    getAllTransactions();
    getPointsBalance();
    getMachines();
    getClubs();
    getPackages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, command?.id]);

  useEffect(() => {
    const newFee = calculateFee({ value, type, machineId: selectedMachineId });
    getQtdInstallments();
    setFee(newFee);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, type, selectedMachineId, payer, installmentCount]);

  const clubServiceOptionsMap = useMemo(() => {
    const map = new Map();

    (clubs || []).forEach((c) => {
      (c?.includedServices || []).forEach((b) => {
        const serviceId = Number(b.serviceId);
        if (!serviceId) return;

        const key = `club-sub:${c.subscriptionId}|plan:${c.clubPlanId}|benefit:${b.benefitId}|service:${serviceId}`;
        const label = `${c.clubPlanName || "Clube"} • ${b.title || "Serviço incluso"} • ${
          b.qty ? `${b.qty}x` : "1x"
        }`;

        const item = {
          key,
          label,
          subscriptionId: c.subscriptionId,
          clubPlanId: c.clubPlanId,
          clubPlanName: c.clubPlanName,
          benefitId: b.benefitId,
          serviceId,
          qty: b.qty ?? 1,
          benefitTitle: b.title || null,
        };

        const arr = map.get(serviceId) || [];
        arr.push(item);
        map.set(serviceId, arr);
      });
    });

    for (const [k, arr] of map.entries()) {
      arr.sort((a, b) => String(a.clubPlanName || "").localeCompare(String(b.clubPlanName || "")));
      map.set(k, arr);
    }

    return map;
  }, [clubs]);

  const genericClubBenefitOptions = useMemo(() => {
    const list = [];

    (clubs || []).forEach((c) => {
      const gbs = Array.isArray(c?.genericBenefits) ? c.genericBenefits : [];

      gbs.forEach((b) => {
        if (!b?.benefitId) return;

        const key = `club-generic-sub:${c.subscriptionId}|plan:${c.clubPlanId}|benefit:${b.benefitId}`;
        const right = benefitValueLabel(b);
        const labelParts = [c.clubPlanName || "Clube", b.title || benefitTypeLabel(b.type), right || null].filter(Boolean);

        list.push({
          key,
          subscriptionId: c.subscriptionId,
          clubPlanId: c.clubPlanId,
          clubPlanName: c.clubPlanName,
          benefitId: b.benefitId,
          benefitType: b.type,
          benefitTitle: b.title,
          benefitDescription: b.description,
          benefitValue: b.value,
          qty: b.qty ?? 1,
          label: labelParts.join(" • "),
        });
      });
    });

    list.sort((a, b) => {
      const byPlan = String(a.clubPlanName || "").localeCompare(String(b.clubPlanName || ""));
      if (byPlan !== 0) return byPlan;
      return String(a.benefitTitle || "").localeCompare(String(b.benefitTitle || ""));
    });

    return list;
  }, [clubs]);

  const packageServiceOptionsMap = useMemo(() => {
    const map = new Map();

    (packages || []).forEach((p) => {
      const items = Array.isArray(p?.items) ? p.items : [];

      items.forEach((item) => {
        const serviceId = Number(item?.serviceId);
        if (!serviceId) return;

        const quantityRemaining = Number(item?.quantityRemaining ?? item?.remainingQuantity ?? item?.quantityTotal ?? 0);
        if (quantityRemaining <= 0) return;

        const key = `package-sale:${p.packageSaleId}|package:${p.packageId}|service:${serviceId}`;
        const label = `${p.packageName || "Pacote"} • ${quantityRemaining} restante(s)${
          p?.expiresAt ? ` • vence ${formatDate(p.expiresAt)}` : ""
        }`;

        const opt = {
          key,
          packageSaleId: p.packageSaleId,
          packageId: p.packageId,
          packageName: p.packageName,
          serviceId,
          serviceName: item.serviceName,
          quantityRemaining,
          expiresAt: p?.expiresAt || null,
          packageItemId: item?.id || null,
          label,
        };

        const arr = map.get(serviceId) || [];
        arr.push(opt);
        map.set(serviceId, arr);
      });
    });

    for (const [k, arr] of map.entries()) {
      arr.sort((a, b) => String(a.packageName || "").localeCompare(String(b.packageName || "")));
      map.set(k, arr);
    }

    return map;
  }, [packages]);

  const genericPackageOptions = useMemo(() => {
    const list = [];

    (packages || []).forEach((p) => {
      const items = Array.isArray(p?.items) ? p.items : [];
      const validItems = items.filter((item) => {
        const quantityRemaining = Number(item?.quantityRemaining ?? item?.remainingQuantity ?? item?.quantityTotal ?? 0);
        return quantityRemaining > 0 && Number(item?.serviceId);
      });

      if (!validItems.length) return;

      const totalRemaining = validItems.reduce(
        (sum, item) => sum + Number(item?.quantityRemaining ?? item?.remainingQuantity ?? item?.quantityTotal ?? 0),
        0
      );

      const key = `package-generic-sale:${p.packageSaleId}|package:${p.packageId}`;

      list.push({
        key,
        packageSaleId: p.packageSaleId,
        packageId: p.packageId,
        packageName: p.packageName,
        expiresAt: p?.expiresAt || null,
        totalRemaining,
        items: validItems.map((item) => ({
          id: item?.id || null,
          serviceId: Number(item?.serviceId),
          serviceName: item?.serviceName,
          quantityRemaining: Number(item?.quantityRemaining ?? item?.remainingQuantity ?? item?.quantityTotal ?? 0),
        })),
        label: `${p.packageName || "Pacote"} • ${totalRemaining} restante(s)${
          p?.expiresAt ? ` • vence ${formatDate(p.expiresAt)}` : ""
        }`,
      });
    });

    list.sort((a, b) => String(a.packageName || "").localeCompare(String(b.packageName || "")));

    return list;
  }, [packages]);

  function getWeekdayName(dateString) {
    const daysOfWeek = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
    const date = new Date(dateString);
    return daysOfWeek[date.getDay()];
  }

  function calculateFee({ value, type, machineId }) {
    if (!value || !type || !machineId || !machines.length) return 0;

    const selectedMachine = machines.find((machine) => machine.id === Number(machineId));
    if (!selectedMachine) return 0;

    let feeRate = 0;

    if (type === "DEBIT_CARD") feeRate = selectedMachine.debitFee || 0;
    if (type === "PIX") feeRate = selectedMachine.pixFee || 0;

    if (type === "CREDIT_CARD") {
      if (selectedMachine?.installmentFees?.length > 0) {
        const found = selectedMachine?.installmentFees.find((f) => f.installments === installmentCount);
        feeRate = found?.feePercentage || 0;
      } else {
        feeRate = selectedMachine.creditFee || selectedMachine.installmentFee || 0;
      }
    }

    return Number(value || 0) * (feeRate / 100);
  }

  function getQtdInstallments() {
    if (!value || !type || type !== "CREDIT_CARD") {
      setInstallment([]);
      return;
    }

    const selectedMachine = machines.find((machine) => machine.id === Number(selectedMachineId));
    if (!selectedMachine) {
      setInstallment([]);
      return;
    }

    const opts = [];

    if (!selectedMachine.installmentFees?.length || selectedMachine.installmentFees?.length === 0) {
      const max = selectedMachine.maxInstallments || 1;
      const f = selectedMachine.installmentFee || 0;

      for (let i = 1; i <= max; i++) {
        const totalComTaxa = Number(value || 0) * (1 + f / 100);
        const parcela = totalComTaxa / i;

        opts.push({
          value: i,
          label: `${i}x de ${formatInReal(parcela)} (total ${formatInReal(totalComTaxa)})`,
        });
      }
    } else {
      selectedMachine.installmentFees.forEach((feeObj) => {
        const taxa = feeObj.feePercentage || 0;
        const totalComTaxa = Number(value || 0) * (1 + taxa / 100);
        const parcela = totalComTaxa / feeObj.installments;

        opts.push({
          value: feeObj.installments,
          label: `${feeObj.installments}x de ${formatInReal(parcela)} (total ${formatInReal(totalComTaxa)})`,
        });
      });
    }

    setInstallment(opts);
  }

  function hasClubForService(serviceId) {
    return (clubServiceOptionsMap.get(Number(serviceId)) || []).length > 0;
  }

  function hasPackageForService(serviceId) {
    return (packageServiceOptionsMap.get(Number(serviceId)) || []).length > 0;
  }

  function hasPointsForService(serviceItem) {
    return Number(serviceItem?.points || 0) > 0;
  }

  function hasAnyGenericClubBenefit() {
    return (genericClubBenefitOptions || []).length > 0;
  }

  function hasAnyGenericPackageBenefit() {
    return (genericPackageOptions || []).length > 0;
  }

  function getServiceAvailability(serviceItem) {
    return {
      package: hasPackageForService(serviceItem?.serviceId),
      club: hasClubForService(serviceItem?.serviceId),
      points: hasPointsForService(serviceItem),
    };
  }

  function getAppliedTransactionForService(serviceItem) {
    if (!serviceItem?.serviceId || !transactions?.length) return null;

    return transactions.find((t) => {
      const sameService = Number(t?.serviceId) === Number(serviceItem?.serviceId);
      const isBenefit = ["POINT", "CLUB", "PACKAGE"].includes(String(t?.type || "").toUpperCase());
      return sameService && isBenefit;
    });
  }

  function getServiceBenefitCount(serviceItem) {
    const availability = getServiceAvailability(serviceItem);
    return [availability.package, availability.club, availability.points].filter(Boolean).length;
  }

  function getRemainingToPay() {
    return Math.max(0, Number(command?.totalValue || 0) - Number(totalPaid || 0));
  }

  const remainingToPay = getRemainingToPay();

  async function getAllTransactions() {
    try {
      setLoadingTransaction(true);
      const response = await commandAPI.getTransactions(command.id);
      const data = Array.isArray(response.data) ? response.data : [];
      setTransactions(data);

      const total = data.reduce((sum, item) => sum + Number(item.value || 0), 0);
      setTotalPaid(total);
    } catch (_) {
      HallAppToast.error("Não foi possível carregar as transações.");
    } finally {
      setLoadingTransaction(false);
    }
  }

  async function getPointsBalance() {
    try {
      setIsLoadingPoints(true);
      const response = await customerRewardsAPI.getPointsBalance(command.whatsapp);
      setPoints(Number(response.data?.points || 0));
    } catch (_) {
      HallAppToast.error("Não foi possível carregar os pontos do cliente.");
    } finally {
      setIsLoadingPoints(false);
    }
  }

  async function getMachines() {
    try {
      setIsLoadingMachine(true);
      const response = await machineAPI.findAllMachine();
      const data = Array.isArray(response.data) ? response.data : [];
      setMachines(data);

      const op = [{ value: "", label: "Selecione uma maquininha" }, ...data.map((m) => ({ value: m.id, label: m.name }))];
      setOptionsMachines(op);
    } catch (_) {
      HallAppToast.genericError();
    } finally {
      setIsLoadingMachine(false);
    }
  }

  async function getClubs() {
    try {
      setIsLoadingClubs(true);
      const response = await schedulingAPI.getClubs(command.id);
      setClubs(Array.isArray(response.data) ? response.data : []);
    } catch (_) {
      setClubs([]);
      HallAppToast.genericError();
    } finally {
      setIsLoadingClubs(false);
    }
  }

  async function getPackages() {
    try {
      setIsLoadingPackages(true);
      const response = await schedulingAPI.getPackages(command.id);
      setPackages(Array.isArray(response.data) ? response.data : []);
    } catch (_) {
      setPackages([]);
    } finally {
      setIsLoadingPackages(false);
    }
  }

  async function getUsableDays(serviceId) {
    try {
      setIsLoadingUsableDays(true);
      const response = await customerRewardsAPI.getPointsUsableDays(serviceId);
      setUsableDays(Array.isArray(response.data) ? response.data : []);
    } catch (_) {
      setUsableDays([]);
      HallAppToast.error("Não foi possível carregar os dias disponíveis para uso dos pontos.");
    } finally {
      setIsLoadingUsableDays(false);
    }
  }

  async function create() {
    const paymentType = type || paymentOptions[0].value;
    const isCardOrPix = ["CREDIT_CARD", "DEBIT_CARD", "PIX"].includes(paymentType);

    const data = {
      type: paymentType,
      value,
      fee: isCardOrPix ? fee : 0,
      payerFee: isCardOrPix ? payer : null,
      installmentCount: paymentType === "CREDIT_CARD" ? installmentCount : null,
      machineId: isCardOrPix ? Number(selectedMachineId || 0) || null : null,
    };

    if (value === null || value === undefined || Number(value) === 0) {
      HallAppToast.error("Informe um valor.");
      return;
    }

    if (!type) {
      HallAppToast.error("Informe o tipo de pagamento.");
      return;
    }


    try {
      setLoadingTransaction(true);
      await commandAPI.createTransaction(command.id, data);
      HallAppToast.success();
      setHasChange(true);

      setValue(0);
      setType(null);
      setSelectedMachineId(null);
      setInstallmentCount(1);
      setFee(0);

      getAllTransactions();
    } catch (e) {
      if (e?.response?.status === 400 && e?.response?.data?.[0]?.code === "COMMAND_VALUE") {
        HallAppToast.error("O valor informado ultrapassa o valor restante da comanda.");
        return;
      }
      HallAppToast.genericError();
    } finally {
      setLoadingTransaction(false);
    }
  }

  function openModalDelete(selectedTransaction) {
    setTransaction(selectedTransaction);
    setShowModalDelete(true);
  }

  async function executeDeletion() {
    try {
      setLoadingModalDelete(true);
      await commandAPI.deleteTransaction(command.id, transaction.id);
      HallAppToast.success();
      setShowModalDelete(false);
      setHasChange(true);
      getAllTransactions();
    } catch (_) {
      HallAppToast.genericError();
    } finally {
      setLoadingModalDelete(false);
    }
  }

  async function openBenefitModal(serviceItem) {
    const applied = getAppliedTransactionForService(serviceItem);
    if (applied) {
      HallAppToast.error("Este serviço já possui um benefício aplicado.");
      return;
    }

    setSelectedService(serviceItem);
    setSelectedBenefitType(null);
    setSelectedBenefitKey(null);
    setUsableDays([]);

    if (hasPointsForService(serviceItem)) {
      await getUsableDays(serviceItem.serviceId);
    }

    setShowBenefitModal(true);
  }

  function closeBenefitModal() {
    setShowBenefitModal(false);
    setSelectedService(null);
    setSelectedBenefitType(null);
    setSelectedBenefitKey(null);
    setUsableDays([]);
  }

  function canUsePointsForSelectedService() {
    if (!selectedService?.serviceId) return false;

    const hasEnoughPoints = Number(points || 0) >= Number(selectedService?.points || 0);
    const validDay = usableDays?.some((day) => day.week === getWeekdayName(command.serviceDate));

    return hasEnoughPoints && validDay;
  }

  async function applySelectedBenefit() {
    if (!selectedService?.serviceId) {
      HallAppToast.error("Serviço inválido.");
      return;
    }

    if (!selectedBenefitType) {
      HallAppToast.error("Selecione um benefício.");
      return;
    }

    try {
      setIsApplyingBenefit(true);

      if (selectedBenefitType === "POINT") {
        if (!canUsePointsForSelectedService()) {
          HallAppToast.error("Os pontos não podem ser usados neste serviço.");
          return;
        }

        const data = {
          type: "POINT",
          value: Number(selectedService.value || 0),
          serviceId: Number(selectedService.serviceId),
        };

        await commandAPI.createTransaction(command.id, data);
        HallAppToast.successWithMessage("Pontos aplicados com sucesso!");
      }

      if (selectedBenefitType === "CLUB") {
        const options = clubServiceOptionsMap.get(Number(selectedService.serviceId)) || [];
        const opt = options.find((o) => o.key === selectedBenefitKey);

        if (!opt) {
          HallAppToast.error("Selecione um clube válido.");
          return;
        }

        const data = {
          type: "CLUB",
          value: Number(selectedService.value || 0),
          serviceId: Number(selectedService.serviceId),
          subscriptionId: opt.subscriptionId,
          clubPlanId: opt.clubPlanId,
          clubPlanBenefitId: opt.benefitId,
        };

        await commandAPI.createTransaction(command.id, data);
        HallAppToast.successWithMessage("Clube aplicado ao serviço!");
      }

      if (selectedBenefitType === "PACKAGE") {
        const options = packageServiceOptionsMap.get(Number(selectedService.serviceId)) || [];
        const opt = options.find((o) => o.key === selectedBenefitKey);

        if (!opt) {
          HallAppToast.error("Selecione um pacote válido.");
          return;
        }

        const data = {
          type: "PACKAGE",
          value: Number(selectedService.value || 0),
          serviceId: Number(selectedService.serviceId),
          packageSaleId: opt.packageSaleId,
          packageId: opt.packageId,
        };

        await commandAPI.createTransaction(command.id, data);
        HallAppToast.successWithMessage("Pacote aplicado ao serviço!");
      }

      setHasChange(true);
      closeBenefitModal();
      getAllTransactions();
      getPackages();
    } catch (_) {
      HallAppToast.genericError();
    } finally {
      setIsApplyingBenefit(false);
    }
  }

  function openGenericClubModal() {
    if (!hasAnyGenericClubBenefit()) {
      HallAppToast.error("Nenhum benefício geral do clube disponível.");
      return;
    }

    if (remainingToPay <= 0) {
      HallAppToast.error("Não há valor a receber nesta comanda.");
      return;
    }

    setSelectedGenericBenefitKey(genericClubBenefitOptions[0]?.key || null);
    setShowModalClubGeneric(true);
  }

  async function confirmGenericClub() {
    if (!selectedGenericBenefitKey) {
      HallAppToast.error("Selecione um benefício do clube.");
      return;
    }

    const opt = genericClubBenefitOptions.find((o) => o.key === selectedGenericBenefitKey);
    if (!opt) {
      HallAppToast.error("Benefício selecionado inválido.");
      return;
    }

    const remaining = getRemainingToPay();
    if (remaining <= 0) {
      HallAppToast.error("Não há valor a receber nesta comanda.");
      return;
    }

    try {
      setIsLoadingClubApply(true);

      const data = {
        type: "CLUB",
        value: remaining,
        subscriptionId: opt.subscriptionId,
        clubPlanId: opt.clubPlanId,
        clubPlanBenefitId: opt.benefitId,
      };

      await commandAPI.createTransaction(command.id, data);

      HallAppToast.successWithMessage("Benefício geral do clube aplicado!");
      setHasChange(true);
      setShowModalClubGeneric(false);
      setSelectedGenericBenefitKey(null);

      getAllTransactions();
    } catch (_) {
      HallAppToast.genericError();
    } finally {
      setIsLoadingClubApply(false);
    }
  }

  function openGenericPackageModal() {
    if (!hasAnyGenericPackageBenefit()) {
      HallAppToast.error("Nenhum pacote geral disponível.");
      return;
    }

    if (remainingToPay <= 0) {
      HallAppToast.error("Não há valor a receber nesta comanda.");
      return;
    }

    setSelectedGenericPackageKey(genericPackageOptions[0]?.key || null);
    setShowModalPackageGeneric(true);
  }

  async function confirmGenericPackage() {
    if (!selectedGenericPackageKey) {
      HallAppToast.error("Selecione um pacote.");
      return;
    }

    const selectedPackage = genericPackageOptions.find((o) => o.key === selectedGenericPackageKey);
    if (!selectedPackage) {
      HallAppToast.error("Pacote selecionado inválido.");
      return;
    }

    try {
      setIsLoadingPackageApply(true);

      const data = {
        packageSaleId: selectedPackage.packageSaleId,
      };

      const response = await commandAPI.applyGeneralPackage(command.id, data);

      const appliedCount = Number(response?.data?.appliedCount || 0);
      const totalApplied = Number(response?.data?.totalApplied || 0);

      if (appliedCount > 0) {
        HallAppToast.successWithMessage(
          `Pacote aplicado com sucesso em ${appliedCount} serviço(s) • total abatido ${formatInReal(totalApplied)}`
        );
      } else {
        HallAppToast.successWithMessage("Pacote aplicado com sucesso!");
      }

      setHasChange(true);
      setShowModalPackageGeneric(false);
      setSelectedGenericPackageKey(null);

      getAllTransactions();
      getPackages();
    } catch (e) {
      const code = e?.response?.data?.code || e?.response?.data?.[0]?.code;

      if (code === "NENHUM_SERVICO_ELEGIVEL_PARA_APLICAR_PACOTE") {
        HallAppToast.error("Nenhum serviço elegível encontrado nesta comanda para o pacote selecionado.");
        return;
      }

      if (code === "PACOTE_SEM_SALDO") {
        HallAppToast.error("Este pacote não possui saldo disponível.");
        return;
      }

      if (code === "PACOTE_EXPIRADO") {
        HallAppToast.error("Este pacote está expirado.");
        return;
      }

      HallAppToast.genericError();
    } finally {
      setIsLoadingPackageApply(false);
    }
  }

  function renderAppliedBadge(serviceItem) {
    const applied = getAppliedTransactionForService(serviceItem);
    if (!applied) return null;

    const currentType = String(applied.type || "").toUpperCase();

    if (currentType === "POINT") {
      return <span className="cp-serviceCard__applied cp-serviceCard__applied--point">Pontos aplicados</span>;
    }

    if (currentType === "CLUB") {
      return <span className="cp-serviceCard__applied cp-serviceCard__applied--club">Clube aplicado</span>;
    }

    if (currentType === "PACKAGE") {
      return <span className="cp-serviceCard__applied cp-serviceCard__applied--package">Pacote aplicado</span>;
    }

    return null;
  }

  function renderAvailabilityChips(serviceItem) {
    const availability = getServiceAvailability(serviceItem);

    return (
      <div className="cp-serviceCard__chips">
        {availability.package && <span className="cp-chip cp-chip--package">Pacote</span>}
        {availability.club && <span className="cp-chip cp-chip--club">Clube</span>}
        {availability.points && <span className="cp-chip cp-chip--points">Pontos</span>}
        {!availability.package && !availability.club && !availability.points && (
          <span className="cp-chip cp-chip--muted">Sem benefícios</span>
        )}
      </div>
    );
  }

  function renderBenefitSelectionBody() {
    if (!selectedService) return null;

    const clubOptions = clubServiceOptionsMap.get(Number(selectedService.serviceId)) || [];
    const packageOptions = packageServiceOptionsMap.get(Number(selectedService.serviceId)) || [];

    const hasPackage = packageOptions.length > 0;
    const hasClub = clubOptions.length > 0;
    const hasPoints = hasPointsForService(selectedService);

    const hasEnoughPoints = Number(points || 0) >= Number(selectedService?.points || 0);
    const validDay = usableDays?.some((day) => day.week === getWeekdayName(command.serviceDate));
    const pointsAllowed = hasEnoughPoints && validDay;

    return (
      <div className="cp-benefitModal">
        <div className="cp-benefitModal__service">
          <div className="cp-benefitModal__serviceName">{selectedService?.serviceName}</div>
          <div className="cp-benefitModal__serviceMeta">
            {displayName(selectedService?.employeeName?.split(" ")[0] || "")} • {formatInReal(selectedService?.value)}
          </div>
        </div>

        {!hasPackage && !hasClub && !hasPoints && (
          <div className="cp-emptyBox">Nenhum benefício disponível para este serviço.</div>
        )}

        {hasPackage && (
          <div className={`cp-optionGroup ${selectedBenefitType === "PACKAGE" ? "is-active" : ""}`}>
            <div className="cp-optionGroup__header">
              <div>
                <div className="cp-optionGroup__title">Pacote</div>
                <div className="cp-optionGroup__subtitle">Use uma sessão do pacote neste serviço</div>
              </div>

              <Form.Check
                type="radio"
                name="selectedBenefitType"
                checked={selectedBenefitType === "PACKAGE"}
                onChange={() => {
                  setSelectedBenefitType("PACKAGE");
                  setSelectedBenefitKey(packageOptions?.[0]?.key || null);
                }}
              />
            </div>

            {selectedBenefitType === "PACKAGE" && (
              <div className="cp-optionGroup__body">
                {packageOptions.map((opt) => (
                  <Form.Check
                    key={opt.key}
                    type="radio"
                    id={opt.key}
                    name="packageOptions"
                    label={opt.label}
                    checked={selectedBenefitKey === opt.key}
                    onChange={() => setSelectedBenefitKey(opt.key)}
                    className="mb-2"
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {hasClub && (
          <div className={`cp-optionGroup ${selectedBenefitType === "CLUB" ? "is-active" : ""}`}>
            <div className="cp-optionGroup__header">
              <div>
                <div className="cp-optionGroup__title">Clube</div>
                <div className="cp-optionGroup__subtitle">Aplique um benefício do clube neste serviço</div>
              </div>

              <Form.Check
                type="radio"
                name="selectedBenefitType"
                checked={selectedBenefitType === "CLUB"}
                onChange={() => {
                  setSelectedBenefitType("CLUB");
                  setSelectedBenefitKey(clubOptions?.[0]?.key || null);
                }}
              />
            </div>

            {selectedBenefitType === "CLUB" && (
              <div className="cp-optionGroup__body">
                {clubOptions.map((opt) => (
                  <Form.Check
                    key={opt.key}
                    type="radio"
                    id={opt.key}
                    name="clubOptions"
                    label={opt.label}
                    checked={selectedBenefitKey === opt.key}
                    onChange={() => setSelectedBenefitKey(opt.key)}
                    className="mb-2"
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {hasPoints && (
          <div className={`cp-optionGroup ${selectedBenefitType === "POINT" ? "is-active" : ""}`}>
            <div className="cp-optionGroup__header">
              <div>
                <div className="cp-optionGroup__title">Pontos</div>
                <div className="cp-optionGroup__subtitle">
                  Cliente possui <b>{points}</b> pontos • este serviço exige <b>{selectedService?.points}</b>
                </div>
              </div>

              <Form.Check
                type="radio"
                name="selectedBenefitType"
                checked={selectedBenefitType === "POINT"}
                onChange={() => {
                  setSelectedBenefitType("POINT");
                  setSelectedBenefitKey("POINT");
                }}
              />
            </div>

            <div className="cp-optionGroup__body">
              {isLoadingUsableDays ? (
                <AppLoading />
              ) : (
                <>
                  <div className="cp-daysBox">
                    <div className="cp-daysBox__title">Dias permitidos para uso com pontos</div>

                    {usableDays?.length > 0 ? (
                      <ul className="cp-daysBox__list">
                        {usableDays.map((u, idx) => (
                          <li key={`${u.week || idx}`}>{u.translate}</li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-muted">Nenhuma regra encontrada.</div>
                    )}
                  </div>

                  {!pointsAllowed && selectedBenefitType === "POINT" && (
                    <Alert variant="danger" className="mb-0 mt-2">
                      Pontos insuficientes ou o serviço não pode ser utilizado com pontos neste dia.
                    </Alert>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <Modal
        className="hallapp-establishment-modal command-payment-modal-v2"
        show={show}
        fullscreen
        onHide={() => closeShow(hasChange)}
      >
        <Modal.Header className="hallapp-establishment-modal custom-close-button" closeButton>
          <Modal.Title className="cp-headerTitle">Pagamento</Modal.Title>
        </Modal.Header>

        <Modal.Body className="hallapp-establishment-modal">
          <CustomContainer>
            <section className="cp-hero">
              <div className="cp-hero__left">
                <div className="cp-hero__title">{truncateText(command?.customerName || "Cliente", 40)}</div>
                <div className="cp-hero__subtitle">
                  {formatDate(command?.serviceDate)} • {command?.employees?.length || 0} serviço(s)
                </div>
              </div>

              <div className="cp-hero__right">
                <div className="cp-pointsBadge">
                  <span className="cp-pointsBadge__label">Pontos</span>
                  <span className="cp-pointsBadge__value">{isLoadingPoints ? "..." : points}</span>
                </div>
              </div>
            </section>

            <section className="cp-summary">
              <div className="cp-summary__card">
                <div className="cp-summary__label">Valor total</div>
                <div className="cp-summary__value">{formatInReal(command?.totalValue)}</div>
              </div>

              <div className="cp-summary__card">
                <div className="cp-summary__label">Pago</div>
                <div className="cp-summary__value">{formatInReal(totalPaid)}</div>
              </div>

              <div className="cp-summary__card cp-summary__card--highlight">
                <div className="cp-summary__label">À receber</div>
                <div className="cp-summary__value">{formatInReal(remainingToPay)}</div>
              </div>
            </section>

            <section className="cp-section">
              <div className="cp-section__header">
                <div>
                  <div className="cp-section__title">Benefícios gerais</div>
                  <div className="cp-section__subtitle">
                    Use benefícios que podem ser aplicados na comanda inteira
                  </div>
                </div>
              </div>

              <div className="cp-generalBenefits">
                <div className="cp-generalBenefits__card">
                  <div className="cp-generalBenefits__meta">
                    <div className="cp-generalBenefits__name">Clube</div>
                    <div className="cp-generalBenefits__desc">
                      {isLoadingClubs
                        ? "Carregando clubes..."
                        : hasAnyGenericClubBenefit()
                        ? `${genericClubBenefitOptions.length} benefício(s) geral(is) disponível(is)`
                        : "Nenhum benefício geral disponível"}
                    </div>
                  </div>

                  <Button
                    variant="outline-success"
                    className="cp-pillBtn"
                    onClick={openGenericClubModal}
                    disabled={isLoadingClubs || remainingToPay <= 0 || !hasAnyGenericClubBenefit()}
                  >
                    Usar clube
                  </Button>
                </div>

                <div className="cp-generalBenefits__card">
                  <div className="cp-generalBenefits__meta">
                    <div className="cp-generalBenefits__name">Pacote</div>
                    <div className="cp-generalBenefits__desc">
                      {isLoadingPackages
                        ? "Carregando pacotes..."
                        : hasAnyGenericPackageBenefit()
                        ? `${genericPackageOptions.length} pacote(s) disponível(is)`
                        : "Nenhum pacote disponível"}
                    </div>
                  </div>

                  <Button
                    variant="outline-primary"
                    className="cp-pillBtn"
                    onClick={openGenericPackageModal}
                    disabled={isLoadingPackages || remainingToPay <= 0 || !hasAnyGenericPackageBenefit()}
                  >
                    Usar pacote
                  </Button>
                </div>
              </div>
            </section>

            <section className="cp-section">
              <div className="cp-section__header">
                <div>
                  <div className="cp-section__title">Serviços realizados</div>
                  <div className="cp-section__subtitle">
                    Toque em um serviço para aplicar pacote, clube ou pontos
                  </div>
                </div>
              </div>

              <div className="cp-serviceList">
                {(command?.employees || []).map((serviceItem, index) => {
                  const applied = getAppliedTransactionForService(serviceItem);
                  const benefitCount = getServiceBenefitCount(serviceItem);

                  return (
                    <div className="cp-serviceCard" key={`${serviceItem.serviceId}-${index}`}>
                      <div className="cp-serviceCard__top">
                        <div className="cp-serviceCard__content">
                          <div className="cp-serviceCard__title">{renderWithTooltip(serviceItem.serviceName, 30)}</div>
                          <div className="cp-serviceCard__meta">
                            {displayName(serviceItem.employeeName?.split(" ")[0] || "")}
                          </div>
                        </div>

                        <div className="cp-serviceCard__price">{formatInReal(serviceItem.value)}</div>
                      </div>

                      <div className="cp-serviceCard__bottom">
                        <div className="cp-serviceCard__left">
                          {renderAvailabilityChips(serviceItem)}
                          {renderAppliedBadge(serviceItem)}
                        </div>

                        <div className="cp-serviceCard__right">
                          <Button
                            variant={applied ? "outline-secondary" : "outline-primary"}
                            className="cp-pillBtn"
                            onClick={() => openBenefitModal(serviceItem)}
                            disabled={!!applied || benefitCount === 0}
                          >
                            {applied ? "Aplicado" : benefitCount > 0 ? "Ver opções" : "Sem opções"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="cp-section">
              <div className="cp-section__header">
                <div>
                  <div className="cp-section__title">Receber pagamento</div>
                  <div className="cp-section__subtitle">Use esta área para receber o valor restante</div>
                </div>
              </div>

              <div className="cp-paymentBox">
                <Form>
                  <Row>
                    <Col xs={12} md={6}>
                      <AppInputSelect
                        options={paymentOptions}
                        label="Tipo de pagamento"
                        value={type || ""}
                        onChange={(e) => setType(e.target.value)}
                      />
                    </Col>

                    <Col xs={12} md={6}>
                      <AppCurrencyInput value={value} label="Valor" onChange={(_, v) => setValue(v)} />
                    </Col>
                  </Row>

                  {(type === "CREDIT_CARD" || type === "DEBIT_CARD" || type === "PIX") && (
                    <div className="cp-paymentBox__advanced">
                      <Row>
                        <Col xs={12}>
                          <AppInputSelect
                            options={optionsMachines}
                            label="Maquininha"
                            value={selectedMachineId || ""}
                            onChange={(e) => setSelectedMachineId(e.target.value)}
                          />
                        </Col>
                      </Row>

                      {type === "CREDIT_CARD" && selectedMachineId && (
                        <Row>
                          <Col xs={12}>
                            <AppInputSelect
                              options={installment}
                              label="Parcelas"
                              value={installmentCount}
                              onChange={(e) => setInstallmentCount(Number(e.target.value))}
                            />
                          </Col>
                        </Row>
                      )}

                      <div className="cp-feeBox">
                        <div className="cp-feeBox__radios">
                          <Form.Check
                            inline
                            type="radio"
                            id="payer-client"
                            label="Cliente paga a taxa"
                            name="taxOption"
                            checked={payer === "CLIENT"}
                            onChange={() => setPayer("CLIENT")}
                          />
                          <Form.Check
                            inline
                            type="radio"
                            id="payer-salon"
                            label="Salão paga a taxa"
                            name="taxOption"
                            checked={payer === "SALON"}
                            onChange={() => setPayer("SALON")}
                          />
                        </div>

                        <div className="cp-feeBox__summary">
                          <div>
                            <span>Taxa</span>
                            <strong>{formatInReal(fee)}</strong>
                          </div>

                          <div>
                            <span>Cliente pagará</span>
                            <strong>
                              {formatInReal(
                                payer === "CLIENT"
                                  ? Number(value || 0) + Number(fee || 0)
                                  : Number(value || 0)
                              )}
                            </strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <AppButton
                    type="button"
                    disabled={isLoadingTransactions || isLoadingMachine}
                    onClick={create}
                    width="100%"
                    name="Adicionar pagamento"
                  />
                </Form>
              </div>
            </section>

            <section className="cp-section">
              {isLoadingTransactions ? (
                <HallAppCenterContainer>
                  <AppLoading />
                </HallAppCenterContainer>
              ) : (
                <Accordion defaultActiveKey="0">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>Histórico de pagamentos</Accordion.Header>
                    <Accordion.Body>
                      {transactions?.length === 0 && (
                        <div className="cp-emptyBox">Nenhum pagamento registrado até o momento.</div>
                      )}

                      {transactions.map((e, idx) => (
                        <ListGroup key={`${e.id || idx}`} className="cp-historyCard">
                          <ListGroup.Item>
                            <div className="d-flex justify-content-between">
                              <div>
                                <strong>Resp. pagamento</strong>
                              </div>
                              <div>{displayName(e?.responsibleCreate || "")}</div>
                            </div>
                          </ListGroup.Item>

                          <ListGroup.Item>
                            <div className="d-flex justify-content-between">
                              <div>
                                <strong>Tipo</strong>
                              </div>
                              <div>{formatTransactionType(e.type)}</div>
                            </div>
                          </ListGroup.Item>

                          <ListGroup.Item>
                            <div className="d-flex justify-content-between">
                              <div>
                                <strong>Valor</strong>
                              </div>
                              <div>{formatInReal(e.value)}</div>
                            </div>
                          </ListGroup.Item>

                          {e.fee > 0 && (
                            <ListGroup.Item>
                              <div className="d-flex justify-content-between">
                                <div>
                                  <strong>Taxa</strong>
                                </div>
                                <div>{formatInReal(e.fee)}</div>
                              </div>
                            </ListGroup.Item>
                          )}

                          {e.fee > 0 && (
                            <ListGroup.Item>
                              <div className="d-flex justify-content-between">
                                <div>
                                  <strong>Valor final</strong>
                                </div>
                                <div>{formatInReal(Number(e.value || 0) + Number(e.fee || 0))}</div>
                              </div>
                            </ListGroup.Item>
                          )}

                          {e.payerFee && (
                            <ListGroup.Item>
                              <div className="d-flex justify-content-between">
                                <div>
                                  <strong>Resp. taxa</strong>
                                </div>
                                <div>{e.payerFee === "CLIENT" ? "Cliente" : "Estabelecimento"}</div>
                              </div>
                            </ListGroup.Item>
                          )}

                          {e.serviceId && (
                            <ListGroup.Item>
                              <div className="d-flex justify-content-between">
                                <div>
                                  <strong>Serviço</strong>
                                </div>
                                <div>{e.serviceName || "—"}</div>
                              </div>
                            </ListGroup.Item>
                          )}

                          <ListGroup.Item>
                            <div className="d-flex justify-content-between">
                              <div>
                                <strong>Data</strong>
                              </div>
                              <div>{formatDateTime(e.createDate)}</div>
                            </div>
                          </ListGroup.Item>

                          <ListGroup.Item>
                            <Button style={{ width: "100%" }} onClick={() => openModalDelete(e)} variant="outline-danger">
                              Excluir
                            </Button>
                          </ListGroup.Item>
                        </ListGroup>
                      ))}
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              )}
            </section>
          </CustomContainer>

          <ConfirmModal
            show={showModalDelete}
            title="Excluir pagamento"
            isLoading={isLoadingModalDelete}
            confirm={executeDeletion}
            closeModal={() => {
              setShowModalDelete(false);
              setTransaction({});
            }}
          >
            Deseja excluir o valor pago no dia {formatDateTime(transaction.createDate)} de{" "}
            {formatInReal(transaction.value)}?
          </ConfirmModal>

          <Modal show={showBenefitModal} onHide={closeBenefitModal} centered contentClassName="hallapp-modal">
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: 15 }}>Aplicar benefício no serviço</Modal.Title>
            </Modal.Header>

            <Modal.Body>{renderBenefitSelectionBody()}</Modal.Body>

            <Modal.Footer>
              <Button variant="outline-secondary" onClick={closeBenefitModal}>
                Cancelar
              </Button>

              <Button variant="primary" onClick={applySelectedBenefit} disabled={!selectedBenefitType || isApplyingBenefit}>
                {isApplyingBenefit ? "Aplicando..." : "Aplicar benefício"}
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal
            show={showModalClubGeneric}
            onHide={() => {
              setShowModalClubGeneric(false);
              setSelectedGenericBenefitKey(null);
            }}
            centered
            contentClassName="hallapp-modal"
          >
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: 15 }}>Aplicar benefício geral do clube</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <div className="cp-benefitModal__service">
                <div className="cp-benefitModal__serviceName">Comanda</div>
                <div className="cp-benefitModal__serviceMeta">À receber: {formatInReal(remainingToPay)}</div>
              </div>

              <div className="cp-optionGroup is-active">
                <div className="cp-optionGroup__header">
                  <div>
                    <div className="cp-optionGroup__title">Selecione um benefício</div>
                    <div className="cp-optionGroup__subtitle">
                      Este benefício será aplicado no valor restante da comanda
                    </div>
                  </div>
                </div>

                <div className="cp-optionGroup__body">
                  {genericClubBenefitOptions.map((opt) => (
                    <Form.Check
                      key={opt.key}
                      type="radio"
                      id={opt.key}
                      name="clubGenericBenefit"
                      label={opt.label}
                      checked={selectedGenericBenefitKey === opt.key}
                      onChange={() => setSelectedGenericBenefitKey(opt.key)}
                      className="mb-2"
                    />
                  ))}

                  {(() => {
                    const selected = genericClubBenefitOptions.find((o) => o.key === selectedGenericBenefitKey);
                    if (!selected?.benefitDescription) return null;

                    return <div className="cp-descriptionBox">{selected.benefitDescription}</div>;
                  })()}
                </div>
              </div>
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setShowModalClubGeneric(false);
                  setSelectedGenericBenefitKey(null);
                }}
              >
                Cancelar
              </Button>

              <Button variant="success" onClick={confirmGenericClub} disabled={isLoadingClubApply}>
                {isLoadingClubApply ? "Aplicando..." : "Aplicar"}
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal
            show={showModalPackageGeneric}
            onHide={() => {
              setShowModalPackageGeneric(false);
              setSelectedGenericPackageKey(null);
            }}
            centered
            contentClassName="hallapp-modal"
          >
            <Modal.Header closeButton>
              <Modal.Title style={{ fontSize: 15 }}>Aplicar pacote geral</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <div className="cp-benefitModal__service">
                <div className="cp-benefitModal__serviceName">Comanda</div>
                <div className="cp-benefitModal__serviceMeta">À receber: {formatInReal(remainingToPay)}</div>
              </div>

              <div className="cp-optionGroup is-active">
                <div className="cp-optionGroup__header">
                  <div>
                    <div className="cp-optionGroup__title">Selecione um pacote</div>
                    <div className="cp-optionGroup__subtitle">
                      O sistema aplicará automaticamente o pacote nos serviços elegíveis da comanda
                    </div>
                  </div>
                </div>

                <div className="cp-optionGroup__body">
                  {genericPackageOptions.map((opt) => (
                    <Form.Check
                      key={opt.key}
                      type="radio"
                      id={opt.key}
                      name="packageGenericBenefit"
                      label={opt.label}
                      checked={selectedGenericPackageKey === opt.key}
                      onChange={() => setSelectedGenericPackageKey(opt.key)}
                      className="mb-2"
                    />
                  ))}

                  {(() => {
                    const selected = genericPackageOptions.find((o) => o.key === selectedGenericPackageKey);
                    if (!selected) return null;

                    return (
                      <div className="cp-descriptionBox">
                        <div><strong>Itens disponíveis:</strong></div>
                        <ul className="mb-0 mt-2">
                          {selected.items.map((item, idx) => (
                            <li key={`${item.serviceId}-${idx}`}>
                              {item.serviceName || "Serviço"} • {item.quantityRemaining} restante(s)
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setShowModalPackageGeneric(false);
                  setSelectedGenericPackageKey(null);
                }}
              >
                Cancelar
              </Button>

              <Button variant="primary" onClick={confirmGenericPackage} disabled={isLoadingPackageApply}>
                {isLoadingPackageApply ? "Aplicando..." : "Aplicar"}
              </Button>
            </Modal.Footer>
          </Modal>

          <FloatingVideoButton
            isSecondFloatingButton={false}
            youtubeUrl={"https://www.youtube.com/embed/60jIdih-L9Q"}
          />
        </Modal.Body>
      </Modal>
    </>
  );
}

export default CommandPaymentModal;